// client/src/coms/components/SoundPackBrowser.js
// Marketplace → Sound Packs: published soundboard packs (free). Search (pack, sounds, keywords, creator,
// server), categories and sorting; preview any sound; install (a link to the creator's pack, not a copy,
// so updates arrive automatically) or uninstall; server managers can enable a pack for a server.
// Paid packs are not available: there is no purchase flow until payments exist.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPost } from '../comsApi';
import { previewSound, stopPreview } from '../soundboard/soundboardEngine';
import TrustDialog from './TrustDialog';
import '../soundboard/soundboard.css';

const CATEGORY_LABEL = { memes: 'Memes', reactions: 'Reactions', music: 'Music', gaming: 'Gaming', effects: 'Sound effects', voices: 'Voices', animals: 'Animals', ambience: 'Ambience', other: 'Other' };
const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '8px 10px', fontSize: 13 };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export default function SoundPackBrowser({ pageSize = 24 }) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('popular');
  const [packs, setPacks] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [playing, setPlaying] = useState('');
  const [trust, setTrust] = useState(null);
  const [busy, setBusy] = useState(false);
  const [servers, setServers] = useState([]);
  const [serverFor, setServerFor] = useState(null); // pack being added to a server
  const seq = useRef(0);

  const search = useCallback(
    async (offset = 0) => {
      const my = (seq.current += 1);
      setLoading(true);
      setErr('');
      const params = new URLSearchParams({ limit: String(pageSize), sort, offset: String(offset) });
      if (q.trim()) params.set('q', q.trim());
      if (category) params.set('category', category);
      try {
        const r = await comsApiGet(`/sounds/discover?${params.toString()}`);
        if (my !== seq.current) return;
        setPacks((cur) => (offset ? [...cur, ...(r.packs || [])] : r.packs || []));
        setNext(r.next_offset);
      } catch (e) {
        if (my === seq.current) setErr(errText(e, 'Could not load sound packs.'));
      } finally {
        if (my === seq.current) setLoading(false);
      }
    },
    [q, category, sort, pageSize]
  );

  useEffect(() => {
    const t = setTimeout(() => search(0), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    comsApiGet('/sounds/collections?all_servers=1')
      .then((r) => setServers((r.servers || []).filter((s) => s.can_manage)))
      .catch(() => setServers([]));
    return () => stopPreview();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  async function preview(s) {
    const key = `${s.collection_id}:${s.id}`;
    if (playing === key) {
      stopPreview();
      setPlaying('');
      return;
    }
    setPlaying(key);
    try {
      await previewSound({ hash: s.hash, url: s.url, volume: s.volume });
    } catch (e) {
      setErr(errText(e, 'Could not play that sound.'));
    } finally {
      setPlaying((k) => (k === key ? '' : k));
    }
  }

  const refreshPack = (p) => setPacks((list) => list.map((x) => (x.id === p.id ? { ...x, ...p } : x)));

  function askInstall(p) {
    setTrust({
      pack: p,
      info: { name: p.name, creator: p.server_name ? `${p.creator} · ${p.server_name}` : p.creator, count: p.sound_count, bytes: (p.sounds || []).reduce((n, s) => n + (s.bytes || 0), 0), types: ['audio/mpeg'], permissions: 'None (audio clips only)' },
    });
  }

  async function install() {
    const p = trust.pack;
    setBusy(true);
    try {
      const r = await comsApiPost(`/sounds/collections/${p.id}/install`, { consent: true });
      refreshPack(r.collection);
      setNotice(`Added “${p.name}” to your sounds.`);
    } catch (e) {
      setErr(errText(e, 'Could not install the pack.'));
    } finally {
      setBusy(false);
      setTrust(null);
    }
  }

  async function uninstall(p) {
    setBusy(true);
    try {
      await comsApiDelete(`/sounds/installs/${p.id}`);
      refreshPack({ id: p.id, installed: false, install_count: Math.max(0, (p.install_count || 1) - 1) });
      setNotice(`Removed “${p.name}”.`);
    } catch (e) {
      setErr(errText(e, 'Could not remove the pack.'));
    } finally {
      setBusy(false);
    }
  }

  async function enableOn(p, serverKey) {
    setBusy(true);
    try {
      await comsApiPost(`/sounds/servers/${encodeURIComponent(serverKey)}/packs`, { collection_id: p.id });
      const s = servers.find((x) => x.key === serverKey);
      setNotice(`Enabled “${p.name}” on ${s ? s.name : 'the server'}.`);
      setServerFor(null);
    } catch (e) {
      setErr(errText(e, 'Could not add the pack to that server.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="sound-pack-browser">
      <div className="vox-spk-filters">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sound packs, sounds or creators" aria-label="Search sound packs" style={{ ...field, flex: '1 1 240px', minWidth: 0 }} />
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort" style={field}>
          <option value="popular">Most installed</option>
          <option value="newest">Newest</option>
          <option value="name">Name</option>
        </select>
      </div>
      <div className="vox-spk-filters" role="group" aria-label="Categories" style={{ marginTop: 8 }}>
        <button type="button" className={`vox-sb-tab`} aria-pressed={!category} onClick={() => setCategory('')}>
          All
        </button>
        {Object.entries(CATEGORY_LABEL).map(([k, l]) => (
          <button key={k} type="button" className="vox-sb-tab" aria-pressed={category === k} onClick={() => setCategory(category === k ? '' : k)}>
            {l}
          </button>
        ))}
      </div>

      {err ? (
        <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>
          {err}
        </div>
      ) : null}
      {notice ? (
        <div className="vox-sbm-notice" role="status" style={{ marginTop: 10 }}>
          {notice}
        </div>
      ) : null}

      <div className="vox-spk-grid">
        {packs.map((p) => (
          <article key={p.id} className="vox-spk-card" data-testid="sound-pack-card" aria-label={p.name}>
            <div className="vox-spk-head">
              <div className="vox-spk-art" aria-hidden="true">
                {p.icon_url ? <img src={p.icon_url} alt="" /> : ((p.sounds || []).find((s) => s.emoji) || {}).emoji || '🔊'}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="vox-spk-name">{p.name}</div>
                <div className="vox-spk-by">
                  by {p.creator || 'someone'}
                  {p.server_name ? ` · ${p.server_name}` : ''}
                  {p.category ? ` · ${CATEGORY_LABEL[p.category] || p.category}` : ''}
                </div>
              </div>
            </div>
            <div className="vox-spk-desc">{p.description || (p.tags && p.tags.length ? p.tags.map((t) => `#${t}`).join(' ') : '')}</div>
            <div className="vox-spk-sounds" aria-label="Preview sounds">
              {(p.sounds || []).slice(0, 8).map((s) => {
                const key = `${p.id}:${s.id}`;
                return (
                  <button key={s.id} type="button" className={`vox-spk-sound${playing === key ? ' on' : ''}`} onClick={() => preview({ ...s, collection_id: p.id })} aria-label={playing === key ? `Stop ${s.name}` : `Preview ${s.name}`} title={s.description || s.name}>
                    <span aria-hidden="true">{playing === key ? '■' : s.emoji || '▶'}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  </button>
                );
              })}
            </div>
            <div className="vox-spk-foot">
              <span className="vox-spk-stat">
                {p.sound_count} sound{p.sound_count === 1 ? '' : 's'} · {p.install_count} install{p.install_count === 1 ? '' : 's'} · Free
              </span>
              {p.is_mine ? (
                <span className="vox-spk-stat" style={{ marginRight: 0 }}>
                  Your pack
                </span>
              ) : p.installed ? (
                <button type="button" className="coms-mini-btn" disabled={busy} onClick={() => uninstall(p)}>
                  Installed · Remove
                </button>
              ) : (
                <button type="button" className="coms-mini-btn primary" disabled={busy} onClick={() => askInstall(p)}>
                  Install
                </button>
              )}
              {servers.length ? (
                serverFor === p.id ? (
                  <select autoFocus defaultValue="" aria-label={`Enable ${p.name} on a server`} style={{ ...field, padding: '5px 8px' }} onChange={(e) => e.target.value && enableOn(p, e.target.value)} onBlur={() => setServerFor(null)}>
                    <option value="">Choose a server…</option>
                    {servers.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button type="button" className="coms-mini-btn" onClick={() => setServerFor(p.id)} title="Let everyone on one of your servers play this pack">
                    Add to server…
                  </button>
                )
              ) : null}
            </div>
          </article>
        ))}
      </div>
      {!loading && !packs.length && !err ? <div className="vox-sb-empty">{q || category ? 'No sound packs match.' : 'No sound packs have been published yet. Publish one from Settings → Soundboard.'}</div> : null}
      {loading ? <div className="vox-sb-empty">Loading…</div> : null}
      {next != null && !loading ? (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button type="button" className="coms-mini-btn" onClick={() => search(next)}>
            Show more
          </button>
        </div>
      ) : null}
      {trust ? <TrustDialog title="Install sound pack?" info={trust.info} busy={busy} onCancel={() => setTrust(null)} onConfirm={install} /> : null}
    </div>
  );
}
